import { useCallback, useEffect, useRef, useState } from 'react';
import { sortEndYearMonthsDesc } from '../utils/performanceMonth';

const normalizePerformanceResponse = response => {
  if (!response || typeof response !== 'object') {
    return response;
  }

  const nestedResponse = response.data;
  const hasNestedPerformancePayload =
    nestedResponse &&
    typeof nestedResponse === 'object' &&
    ('selected_end_year_month' in nestedResponse ||
      'available_end_year_months' in nestedResponse ||
      'data' in nestedResponse);

  return hasNestedPerformancePayload ? nestedResponse : response;
};

const applyPerformanceResponse = (
  response,
  fallbackEndYearMonth,
  setAvailableEndYearMonths,
  setSelectedEndYearMonth,
  setData,
  setInsightFormatVersion
) => {
  const nextSelectedEndYearMonth =
    response?.selected_end_year_month || fallbackEndYearMonth || '';
  const nextAvailableEndYearMonths = sortEndYearMonthsDesc([
    ...(response?.available_end_year_months || []),
    nextSelectedEndYearMonth
  ]);

  setAvailableEndYearMonths(nextAvailableEndYearMonths);
  setSelectedEndYearMonth(nextSelectedEndYearMonth);
  setData(response?.data ?? null);
  setInsightFormatVersion(response?.insight_format_version || '');
};

const getSelectedEndYearMonth = (response, fallbackEndYearMonth) =>
  response?.selected_end_year_month || fallbackEndYearMonth || '';

const usePerformanceMonthFilter = fetcher => {
  const [selectedEndYearMonth, setSelectedEndYearMonth] = useState('');
  const [availableEndYearMonths, setAvailableEndYearMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [insightFormatVersion, setInsightFormatVersion] = useState('');
  const [screenState, setScreenState] = useState('');
  const [screenStateMeta, setScreenStateMeta] = useState(null);
  const requestIdRef = useRef(0);
  const refreshTimerRef = useRef(null);

  const fetchMonthData = useCallback(
    async endYearMonth => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setLoading(true);

      try {
        let response = normalizePerformanceResponse(
          await fetcher(endYearMonth)
        );

        if (requestIdRef.current !== requestId) {
          return;
        }

        const nextSelectedEndYearMonth = getSelectedEndYearMonth(
          response,
          endYearMonth
        );

        // On the initial load, follow the backend-selected month with
        // an explicit month-scoped fetch before resolving loading/empty state.
        if (
          !endYearMonth &&
          nextSelectedEndYearMonth &&
          response?.selected_end_year_month
        ) {
          response = normalizePerformanceResponse(
            await fetcher(nextSelectedEndYearMonth)
          );

          if (requestIdRef.current !== requestId) {
            return;
          }
        }

        applyPerformanceResponse(
          response,
          endYearMonth,
          setAvailableEndYearMonths,
          setSelectedEndYearMonth,
          setData,
          setInsightFormatVersion
        );
        setScreenState(response?.screen_state || '');
        setScreenStateMeta(response?.screen_state_meta || null);
      } catch (error) {
        if (requestIdRef.current !== requestId) {
          return;
        }

        console.error('Error fetching performance data:', error);

        const errorResponse = normalizePerformanceResponse(
          error?.response?.data || error?.errorData
        );

        if (errorResponse) {
          applyPerformanceResponse(
            errorResponse,
            endYearMonth,
            setAvailableEndYearMonths,
            setSelectedEndYearMonth,
            setData,
            setInsightFormatVersion
          );
          setScreenState(errorResponse?.screen_state || '');
          setScreenStateMeta(errorResponse?.screen_state_meta || null);
        } else {
          setData(null);
          setInsightFormatVersion('');
          setScreenState('');
          setScreenStateMeta(null);
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [fetcher]
  );

  useEffect(() => {
    fetchMonthData();
  }, [fetchMonthData]);

  useEffect(() => {
    const isProgressState = [
      'historical_analysis_in_progress',
      'analysis_pending'
    ].includes(screenState);
    const pollAfterSeconds = Number(screenStateMeta?.poll_after_seconds || 0);

    if (
      !isProgressState ||
      !Number.isFinite(pollAfterSeconds) ||
      pollAfterSeconds <= 0
    ) {
      return undefined;
    }

    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      fetchMonthData(selectedEndYearMonth || undefined);
    }, pollAfterSeconds * 1000);

    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [fetchMonthData, screenState, screenStateMeta, selectedEndYearMonth]);

  const handleEndYearMonthChange = useCallback(
    nextEndYearMonth => {
      if (!nextEndYearMonth) {
        return;
      }

      setSelectedEndYearMonth(nextEndYearMonth);
      fetchMonthData(nextEndYearMonth);
    },
    [fetchMonthData]
  );

  return {
    selectedEndYearMonth,
    availableEndYearMonths,
    loading,
    data,
    insightFormatVersion,
    screenState,
    screenStateMeta,
    handleEndYearMonthChange
  };
};

export default usePerformanceMonthFilter;
